import React, { useState, useEffect } from 'react';
import { lockScroll, unlockScroll } from '../utils/scrollbarUtils';
import { fetchTableOrderDetail } from '../utils/unionApi';
import { getOKDCOrderInfo } from '../utils/okdcApi';
import { getMetacityOrderInfo } from '../utils/metacityApi';
import { getTossOrderInfo } from '../utils/tossApi';
import { fetchWowposOrderHistory, resetWowposTable, cancelWowposTable } from '../utils/wowposApi';
import { simApi } from '../utils/simApi';
import api from '../utils/api';
import orderListIcon from '../assets/icons/common/order_list_icon.png';
import emptyOrderlistIcon from '../assets/icons/common/empty_orderlist_icon.png';
import bottomNavBg from '../assets/icons/common/bottomNav.png';

const OrderHistory = ({ isOpen, onClose, skipAnimation, selectedLanguage = 'ko', onReorder, tableCode = '1', tableName = null, storeId: propStoreId = null, posType: propPosType = null, isSimulation = false, bottomButtonLabel = null, hideOrderButton = false, showResetButton = false, showCancelButton = false }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [orders, setOrders] = useState([]);
  const [menuLanguageMap, setMenuLanguageMap] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  // 주문 취소 = 테이블 초기화 + 해당 세션 매출 영구 삭제 (관리자 wowpos 전용).
  const handleCancelOrder = async () => {
    if (cancelling) return;
    const ok = window.confirm('이 테이블 주문을 취소하시겠습니까?\n매출에서 제외되며 되돌릴 수 없습니다.');
    if (!ok) return;
    setCancelling(true);
    try {
      const r = await cancelWowposTable(propStoreId, tableCode);
      if (!r?.success) throw new Error(r?.error || '주문 취소 실패');
    } catch (e) {
      console.error('[주문내역] 주문 취소 실패:', e);
    } finally {
      setCancelling(false);
      onClose(false);
    }
  };
  
  // UI 텍스트 번역
  const translations = {
    ko: {
      table: ' 테이블',
      orderTitle: '주문내역',
      orderSubtitle: '맛있게 드시고 카운터에서 결제해주세요!',
      total: '총',
      backToMenu: '메뉴판으로 이동',
      emptyOrdersText: '주문한 메뉴가 없어요',
      orderButton: '주문하기',
      orderNumber: '주문번호',
      payLater: '후결제',
      orderAmount: '주문금액',
      quantityFormat: '개',
      weekdays: ['일', '월', '화', '수', '목', '금', '토']
    },
    en: {
      table: 'Table ',
      orderTitle: 'Orders',
      orderSubtitle: 'Enjoy your meal and pay at the counter!',
      total: 'Total',
      backToMenu: 'Back to Menu',
      emptyOrdersText: 'No orders yet',
      orderButton: 'Order Now',
      orderNumber: 'Order #',
      payLater: 'Pay Later',
      orderAmount: 'Order Total',
      quantityFormat: 'X',
      weekdays: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
    },
    zh: {
      table: '号桌',
      orderTitle: '订单记录',
      orderSubtitle: '请慢用，然后在柜台结账！',
      total: '总计',
      backToMenu: '返回菜单',
      emptyOrdersText: '还没有订单',
      orderButton: '立即下单',
      orderNumber: 'Order #',
      payLater: '后付款',
      orderAmount: '订单金额',
      quantityFormat: 'X',
      weekdays: ['日', '一', '二', '三', '四', '五', '六']
    },
    ja: {
      table: 'テーブル',
      orderTitle: '注文内容',
      orderSubtitle: 'お食事後、カウンターでお会計をお願いします！',
      total: '合計',
      backToMenu: 'メニューに戻る',
      emptyOrdersText: 'まだ注文がありません',
      orderButton: '注文する',
      orderNumber: 'Order #',
      payLater: '後払い',
      orderAmount: '注文金額',
      quantityFormat: 'X',
      weekdays: ['日', '月', '火', '水', '木', '金', '土']
    }
  };
  
  const t = translations[selectedLanguage] || translations.ko;

  useEffect(() => {
    if (isOpen) {
      console.log('🎯 === 주문 내역 페이지 열림 ===');
      setIsVisible(true);

      // body 스크롤 방지
      lockScroll();
      
      // 모달 열림 시 body에 클래스 추가
      document.body.classList.add('modal-open');
      
      // 히스토리 상태 추가
      window.history.pushState({ orderHistory: true }, '');
      
      // 애니메이션 건너뛰기 여부 확인
      if (skipAnimation) {
        // 애니메이션 없이 바로 표시
        setIsAnimating(true);
      } else {
        // 일반적인 애니메이션 시작
        setIsAnimating(false);
        setTimeout(() => {
          setIsAnimating(true);
        }, 100);
      }
      
      // 주문 내역 로드
      loadOrderHistory();
    } else {
      setIsAnimating(false);
      
      const timer = setTimeout(() => {
        setIsVisible(false);
        // 애니메이션이 완료된 후 body 스크롤 복원
        unlockScroll();
        // 모달 닫힘 시 body 클래스 제거
        document.body.classList.remove('modal-open');
      }, 150);
      return () => clearTimeout(timer);
    }
  }, [isOpen, skipAnimation, selectedLanguage]);

  useEffect(() => {
    const handlePopState = (e) => {
      if (isOpen) {
        e.preventDefault();
        onClose(false);
      }
    };

    window.addEventListener('popstate', handlePopState);
    
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [isOpen, onClose]);

  useEffect(() => {
    const scaleUI = () => {
      const wrapper = document.querySelector('.order-history-wrapper');
      if (wrapper) {
        const viewportWidth = window.innerWidth;
        const scale = Math.min(viewportWidth / 360, 1.5);
        wrapper.style.zoom = scale;
      }
    };
    
    if (isVisible) {
      scaleUI();
      window.addEventListener('resize', scaleUI);
    }
    
    return () => {
      window.removeEventListener('resize', scaleUI);
    };
  }, [isVisible]);


  const loadOrderHistory = async () => {
    console.log('📋 === loadOrderHistory 함수 시작 ===');
    setIsLoading(true);
    setOrders([]); // 기존 데이터 초기화

    // 로컬 변수로 언어 맵 관리 (state는 비동기 업데이트되므로 같은 함수 내에서 사용 불가)
    let localLanguageMap = {};

    try {
      // 메뉴 데이터 및 매장 정보 가져오기
      let storeInfo = null;
      let storeId = null;

      try {
        // Props로 전달된 storeId 사용, 없으면 getStoreId() 호출
        storeId = propStoreId || await api.getStoreId();
        console.log('🏪 현재 storeId:', storeId, propStoreId ? '(Props에서)' : '(Token에서)');
      } catch (storeIdError) {
        console.error('❌ storeId 조회 실패:', storeIdError);
        setOrders([]);
        setIsLoading(false);
        return;
      }

      if (!storeId) {
        console.log('⚠️ storeId가 없습니다.');
        setOrders([]);
        setIsLoading(false);
        return;
      }

      try {
        const menuResponse = await api.getMenus(storeId);
        if (menuResponse.success && menuResponse.categories) {
          menuResponse.categories.forEach(category => {
            category.menus.forEach(menu => {
              // menu_code를 키로 사용하여 언어별 이름 저장
              localLanguageMap[menu.menu_code] = {
                name_ko: menu.name_ko || menu.menu_name,
                name_en: menu.name_en || menu.menu_name,
                name_ja: menu.name_ja || menu.menu_name,
                name_zh: menu.name_zh || menu.menu_name
              };

              // 옵션 선택지도 언어 맵에 추가 (UnionPOS 주문내역 옵션 번역용)
              // 같은 option_code가 여러 메뉴에 존재할 수 있으므로 menu_code:option_code 복합키 사용
              if (menu.options && Array.isArray(menu.options)) {
                menu.options.forEach(group => {
                  const choices = group.choices || group.items || [];
                  choices.forEach(choice => {
                    const optionCode = choice.itemCode || choice.option_id;
                    if (optionCode) {
                      const compositeKey = `${menu.menu_code}:${optionCode}`;
                      localLanguageMap[compositeKey] = {
                        name_ko: choice.option_name_ko || choice.name || choice.option_name,
                        name_en: choice.option_name_en || choice.name || choice.option_name,
                        name_ja: choice.option_name_ja || choice.name || choice.option_name,
                        name_zh: choice.option_name_zh || choice.name || choice.option_name
                      };
                    }
                  });
                });
              }
            });
          });
          setMenuLanguageMap(localLanguageMap);
          console.log('📚 메뉴+옵션 언어 맵 생성:', Object.keys(localLanguageMap).length, '개');
        }
      } catch (menuError) {
        console.error('메뉴 데이터 로드 실패:', menuError);
        // 메뉴 언어 맵은 필수가 아니므로 계속 진행
      }

      try {
        // 매장 정보 가져오기 (storeId 전달)
        const storeResponse = await api.getStoreInfo(storeId);
        console.log('🏪 매장 정보 응답:', storeResponse);
        if (storeResponse.success && storeResponse.store) {
          storeInfo = storeResponse.store;
          console.log('🏪 매장 정보:', { pos_type: storeInfo.pos_type, storeId: storeInfo.id });
        } else {
          console.log('⚠️ 매장 정보 조회 실패 또는 데이터 없음');
        }
      } catch (storeError) {
        console.error('매장 정보 로드 실패:', storeError);
      }

      console.log('🔍 storeInfo 최종 값:', storeInfo);

      // OKDC 매장인 경우 OKDC API 사용
      if (storeInfo && storeInfo.pos_type === 'okdc') {
        console.log('🔄 === OKDC 주문내역 조회 시작 ===');
        console.log('🔍 사용할 테이블 코드:', tableCode);
        try {
          const okdcResult = await getOKDCOrderInfo(storeInfo.id, tableCode);
          console.log('📥 OKDC 주문내역 응답:', okdcResult);

          if (okdcResult.success && okdcResult.orders.length > 0) {
            // OKDC 주문내역 데이터를 기존 형식으로 변환
            const okdcOrders = okdcResult.orders.filter(order => order.items.length > 0);

            if (okdcOrders.length > 0) {
              const formattedOrders = okdcOrders.map(order => ({
                orderNumber: order.orderNo,
                externalOrderNo: order.externalOrderNo || '',
                timestamp: new Date().toISOString(),
                items: order.items.map(item => {
                  return {
                    name: item.prodName,
                    name_ko: localLanguageMap[item.prodCode]?.name_ko || item.prodName,
                    name_en: localLanguageMap[item.prodCode]?.name_en || item.prodName,
                    name_ja: localLanguageMap[item.prodCode]?.name_ja || item.prodName,
                    name_zh: localLanguageMap[item.prodCode]?.name_zh || item.prodName,
                    price: item.prodAmount,
                    totalPrice: item.totalPrice || item.dcmAmount, // OKDC는 totalPrice 사용 (메뉴+옵션)
                    quantity: item.prodCount,
                    itemCode: item.prodCode,
                    selectedOptions: item.selectedOptions || [] // OKDC 옵션 추가
                  };
                })
              }));

              console.log('✅ === OKDC 가공된 주문 데이터 ===');
              console.log('formattedOrders:', JSON.stringify(formattedOrders, null, 2));
              setOrders(formattedOrders);
              setIsLoading(false);
              return;
            }
          }

          // 주문 데이터가 없는 경우
          setOrders([]);
          setIsLoading(false);
          return;
        } catch (error) {
          console.error('OKDC 주문내역 조회 실패:', error);
          setOrders([]);
          setIsLoading(false);
          return;
        }
      }

      // METACITY 매장: POS-live 8060 조회 (POS_ITEM_ORDER_NO 주문번호 + SETITEM 옵션)
      if (storeInfo && storeInfo.pos_type === 'metacity') {
        console.log('🔄 === METACITY 주문내역 조회 시작 ===');
        try {
          const mcResult = await getMetacityOrderInfo(storeInfo.id, tableCode);
          if (mcResult.success && mcResult.orders.length > 0) {
            const mcOrders = mcResult.orders.filter(order => order.items.length > 0);
            if (mcOrders.length > 0) {
              const formattedOrders = mcOrders.map(order => ({
                orderNumber: order.orderNo,
                externalOrderNo: order.externalOrderNo || '',
                timestamp: new Date().toISOString(),
                items: order.items.map(item => ({
                  name: item.prodName,
                  name_ko: item.prodName,
                  name_en: item.prodName,
                  name_ja: item.prodName,
                  name_zh: item.prodName,
                  price: item.prodAmount,
                  totalPrice: item.totalPrice,
                  quantity: item.prodCount,
                  itemCode: item.prodCode,
                  selectedOptions: item.selectedOptions || []
                }))
              }));
              setOrders(formattedOrders);
              setIsLoading(false);
              return;
            }
          }
          setOrders([]);
          setIsLoading(false);
          return;
        } catch (error) {
          console.error('METACITY 주문내역 조회 실패:', error);
          setOrders([]);
          setIsLoading(false);
          return;
        }
      }

      // 토스 매장: 우리 DB(toss_orders) 조회. 워커가 주문/결제 이벤트를 적재해 두므로
      // POS 에 물을 필요가 없고 SDK rate limit 도 쓰지 않는다.
      if (storeInfo && storeInfo.pos_type === 'toss') {
        console.log('🔄 === 토스 주문내역 조회 시작 ===');
        try {
          const tsResult = await getTossOrderInfo(storeInfo.id, tableCode);
          const tsOrders = (tsResult.orders || []).filter(o => (o.items || []).length > 0);
          setOrders(tsOrders.map(order => ({
            orderNumber: order.orderNo,
            externalOrderNo: '',
            timestamp: order.orderedAt || new Date().toISOString(),
            // 표시 규칙(와우포스·OKDC·유니온과 동일): price = 옵션 제외 기본 단가(이름 밑),
            // totalPrice = 옵션 포함 단가(화면이 × 수량으로 줄 합계·총액 계산), selectedOptions = [{name, price, quantity}].
            // 서버(/api/toss/order-info)가 옵션을 이 형태로 정리하고 unitTotal 을 계산해 내려준다.
            items: (order.items || []).map(item => ({
              name: item.name,
              name_ko: item.name,
              name_en: item.name,
              name_ja: item.name,
              name_zh: item.name,
              price: Number(item.price) || 0,
              totalPrice: Number(item.unitTotal ?? item.price) || 0,
              quantity: item.quantity,
              itemCode: item.itemId,
              selectedOptions: (item.options || []).map((o) => ({
                name: o.name,
                price: Number(o.price) || 0,
                quantity: Number(o.qty) || 1,
              })),
            })),
          })));
          setIsLoading(false);
          return;
        } catch (error) {
          console.error('토스 주문내역 조회 실패:', error);
          setOrders([]);
          setIsLoading(false);
          return;
        }
      }

      // WowPos 매장: 주문 데이터의 마스터는 매장 PC → 백엔드가 WS 로 PC 에 조회.
      if (storeInfo && storeInfo.pos_type === 'wowpos') {
        console.log('🔄 === WowPos 주문내역 조회 시작 ===');
        try {
          const wpResult = await fetchWowposOrderHistory(storeInfo.id, tableCode);
          console.log('📥 WowPos 주문내역 응답:', wpResult);

          const wpOrders = (wpResult.orders || []).filter(o => Array.isArray(o.items) && o.items.length > 0);
          // 와우포스: 매장 PC OrderCounter 가 발급한 테이블당 번호 (날짜별 1부터, 추가 주문도 같은 번호).
          // 같은 테이블의 여러 주문(추가 주문 포함)을 1개 카드로 합치고, 같은 메뉴는 menu_code 기준 그룹화.
          const wpOrderNumber = wpResult.orderNumber || 0;

          // 모든 주문의 items 를 menu_code (or name) 기준으로 그룹화 + qty/총액 합산
          const mergedMap = new Map();
          let firstReceivedAt = null;
          for (const order of wpOrders) {
            if (!firstReceivedAt && order.receivedAt) firstReceivedAt = order.receivedAt;
            for (const item of order.items) {
              // PC 저장 raw JSON 은 'qty' 키 (백엔드 매핑 결과). 'quantity' fallback 도 지원.
              const qty = Number(item.qty ?? item.quantity) || 1;
              const lineTotal = Number(item.price) || 0; // wowpos: price = (기본가+옵션가) × 수량
              // 사은품(is_review_benefit) 은 wowpos_review_benefit_items.id 기반이라
              // 정규 메뉴(wowpos_menus.id) 와 id 가 충돌할 수 있음. 별도 prefix 로 네임스페이스 분리.
              const menuCode = item.menuId
                ? (item.is_review_benefit ? `WP_RB_${item.menuId}` : `WP_${item.menuId}`)
                : `name:${item.name || ''}`;
              // 병합 키에 옵션 조합을 포함한다 — 같은 메뉴라도 옵션이 다르면 다른 줄이어야 한다.
              // (예전엔 menuCode 만으로 합쳐, 옵션 다 넣은 주문과 옵션 없는 주문이
              //  한 줄 '수량 2' 로 뭉쳐 단가까지 평균값으로 뭉개졌다)
              // 순서만 다른 조합은 정렬해 같은 키가 되게 한다. union/metacity 와 동일 규칙.
              const optionKey = (item.options || [])
                .map((o) => `${o.name}_${Number(o.price) || 0}_${Number(o.qty ?? o.quantity) || 1}`)
                .sort()
                .join('|');
              const mergeKey = `${menuCode}__${optionKey}`;
              // OKDC/Union 표시 컨벤션: totalPrice = 단가(옵션 포함). 표시 시 × quantity.
              // 누적 합산 시 cumTotal = 기존_단가 × 기존_수량 + lineTotal → 평균 단가 재계산.
              // 단위당 옵션가 합 = Σ(옵션가 × 옵션수량). wowpos item.price=(기본가+옵션가)×수량 이므로
              // 기본 단가 = 단가(옵션포함) - 옵션가 합. OKDC/Union/metacity 와 동일하게 price=기본가로 통일.
              const optionsPerUnit = (item.options || []).reduce((s, o) => s + (Number(o.price) || 0) * (Number(o.qty ?? o.quantity) || 1), 0);
              if (mergedMap.has(mergeKey)) {
                const existing = mergedMap.get(mergeKey);
                const cumTotal = (existing.totalPrice || 0) * existing.quantity + lineTotal;
                existing.quantity += qty;
                const newUnit = existing.quantity > 0 ? Math.round(cumTotal / existing.quantity) : cumTotal;
                existing.price = Math.max(0, newUnit - optionsPerUnit); // 기본 단가(옵션 제외) — 이름 밑 표시
                existing.totalPrice = newUnit;                          // 옵션 포함 단가 — 라인합계·총액·매출(×수량)
              } else {
                const unit = qty > 0 ? Math.round(lineTotal / qty) : lineTotal;
                const baseUnit = Math.max(0, unit - optionsPerUnit);
                mergedMap.set(mergeKey, {
                  name: item.name,
                  name_ko: localLanguageMap[menuCode]?.name_ko || item.name,
                  name_en: localLanguageMap[menuCode]?.name_en || item.name,
                  name_ja: localLanguageMap[menuCode]?.name_ja || item.name,
                  name_zh: localLanguageMap[menuCode]?.name_zh || item.name,
                  price: baseUnit,  // 기본 단가(옵션 제외) — 이름 밑 표시용
                  totalPrice: unit, // 옵션 포함 단가 — 라인합계·총액·매출 (표시 시 × quantity)
                  quantity: qty,
                  itemCode: menuCode,
                  // 백엔드 item.options([{name,qty,price}]) → 표시용 selectedOptions.
                  selectedOptions: (item.options || []).map((o) => ({
                    name: o.name,
                    price: Number(o.price) || 0,
                    quantity: Number(o.qty) || 1,
                  })),
                });
              }
            }
          }

          const formattedOrders = mergedMap.size === 0 ? [] : [{
            orderNumber: wpOrderNumber || 1,
            externalOrderNo: '',
            timestamp: firstReceivedAt
              ? new Date(firstReceivedAt).toISOString()
              : new Date().toISOString(),
            items: Array.from(mergedMap.values()),
          }];

          setOrders(formattedOrders);
          setIsLoading(false);
          return;
        } catch (error) {
          console.error('WowPos 주문내역 조회 실패:', error);
          setOrders([]);
          setIsLoading(false);
          return;
        }
      }

      // 유니온포스 매장인 경우 기존 API 사용
      console.log('🔄 === Union 주문내역 조회 시작 ===');

      // storeInfo가 없으면 로딩 종료
      if (!storeInfo) {
        console.log('⚠️ 매장 정보가 없어 주문내역을 조회할 수 없습니다.');
        setOrders([]);
        setIsLoading(false);
        return;
      }

      console.log('🔍 매장 ID:', storeInfo.id);
      console.log('🔍 사용할 테이블 코드:', tableCode);
      console.log('🔍 테이블 코드 타입:', typeof tableCode);
      try {
        // Union API에서 주문 내역 조회 (세션의 테이블 코드 사용).
        // 시뮬 매장은 /api/simulation/order/detail (Redis sim_orders 반환).
        const orderDetail = isSimulation
          ? await simApi.fetchTableOrderDetail()
          : await fetchTableOrderDetail(tableCode);
        console.log('📥 Union API fetchTableOrderDetail 응답:');
        console.log('  - success:', orderDetail.success);
        console.log('  - orderList 개수:', orderDetail.orderList?.length || 0);
        console.log('  - orderSum:', orderDetail.orderSum);
        console.log('  - message:', orderDetail.message);

        if (orderDetail.success && orderDetail.orderList.length > 0) {
          // API 원본 데이터 출력
          console.log('🔍 === Union API 주문 내역 원본 데이터 ===');
          console.log('전체 응답:', JSON.stringify(orderDetail, null, 2));
          console.log('orderSum:', orderDetail.orderSum);
          console.log('orderList:', orderDetail.orderList);

          // 각 주문 아이템 상세 출력
          orderDetail.orderList.forEach((item, index) => {
            console.log(`\n📦 주문 아이템 ${index + 1}:`);
            console.log('  ItemName:', item.ItemName);
            console.log('  ItemCode:', item.ItemCode);
            console.log('  Qty:', item.Qty);
            console.log('  TotalAmt:', item.TotalAmt);
            console.log('  개당 가격:', item.TotalAmt / item.Qty);

            if (item.OrderSetList && item.OrderSetList.length > 0) {
              console.log('  🍱 세트 구성품:');
              item.OrderSetList.forEach((setItem, setIndex) => {
                console.log(`    ${setIndex + 1}. ${setItem.ItemName}`);
                console.log(`       - Price: ${setItem.Price}`);
                console.log(`       - Qty: ${setItem.Qty}`);
                console.log(`       - MenuType: ${setItem.MenuType}`);
                console.log(`       - ItemCode: ${setItem.ItemCode}`);
              });
            }
          });
          console.log('=====================================\n');

          // API 데이터를 기존 형식으로 변환
          // orderList를 오래된 순서로 정렬 (OrderDate 기준)
          const sortedOrderList = [...orderDetail.orderList].sort((a, b) => {
            if (a.OrderDate && b.OrderDate) {
              return new Date(a.OrderDate) - new Date(b.OrderDate); // 오래된 순
            }
            return 0;
          });

          // 동일한 메뉴와 옵션을 가진 항목들을 합치기 위한 Map
          const mergedItemsMap = new Map();

          sortedOrderList.forEach(item => {
            // 변동세트인 경우 옵션 가격을 빼서 단품 가격 계산
            let unitPrice = item.TotalAmt / item.Qty;

            // 세트 메뉴이고 변동세트인 경우
            if (item.OrderSetList && item.OrderSetList.length > 0 &&
                item.OrderSetList[0].MenuType === '변동세트') {
              // 옵션 가격 합계 계산
              const optionsTotalPrice = item.OrderSetList.reduce((sum, setItem) => {
                return sum + (setItem.Price || 0);
              }, 0);

              // 단품 가격 = 총액 - 옵션 가격 합계
              unitPrice = (item.TotalAmt / item.Qty) - optionsTotalPrice;

              console.log(`💲 변동세트 가격 계산: ${item.ItemName}`);
              console.log(`   TotalAmt: ${item.TotalAmt}, 옵션합계: ${optionsTotalPrice}, 단품가격: ${unitPrice}`);
            }

            // 고유 키 생성 (메뉴코드 + 옵션 정보)
            let uniqueKey = item.ItemCode;

            // 옵션이 있는 경우 옵션 정보를 키에 추가
            if (item.OrderSetList && item.OrderSetList.length > 0) {
              // 옵션을 정렬하여 순서에 상관없이 동일한 키 생성
              const optionKeys = item.OrderSetList
                .map(setItem => `${setItem.ItemCode}_${setItem.Price}`)
                .sort()
                .join('|');
              uniqueKey += `_${optionKeys}`;
            }

            // 기존에 같은 메뉴가 있는지 확인
            if (mergedItemsMap.has(uniqueKey)) {
              // 있으면 수량 추가
              const existingItem = mergedItemsMap.get(uniqueKey);
              existingItem.quantity += item.Qty;
              console.log(`🔄 메뉴 병합: ${item.ItemName}, 수량 ${existingItem.quantity - item.Qty} + ${item.Qty} = ${existingItem.quantity}`);
            } else {
              // 없으면 새로 추가
              const menuItem = {
                name: item.ItemName,
                // 언어별 이름 추가 (ItemCode로 매핑)
                name_ko: item.ItemName, // 기본값
                name_en: item.ItemName,
                name_ja: item.ItemName,
                name_zh: item.ItemName,
                price: unitPrice, // 계산된 단품 가격
                totalPrice: item.TotalAmt / item.Qty, // 옵션 포함 최종가격
                quantity: item.Qty,
                itemCode: item.ItemCode,
                orderDate: item.OrderDate
              };

              // 세트 메뉴인 경우 옵션 처리
              if (item.OrderSetList && item.OrderSetList.length > 0) {
                menuItem.menuType = item.OrderSetList[0].MenuType; // 고정세트 or 변동세트
                menuItem.selectedOptions = item.OrderSetList.map(setItem => ({
                  name: setItem.ItemName,
                  price: setItem.Price,
                  quantity: setItem.Qty,
                  itemCode: setItem.ItemCode
                }));
              }

              mergedItemsMap.set(uniqueKey, menuItem);
            }
          });

          // localLanguageMap을 사용하여 언어별 이름 설정 (메뉴 + 옵션)
          const itemsWithLanguage = Array.from(mergedItemsMap.values()).map(item => {
            const updatedItem = { ...item };

            if (localLanguageMap[item.itemCode]) {
              updatedItem.name_ko = localLanguageMap[item.itemCode].name_ko;
              updatedItem.name_en = localLanguageMap[item.itemCode].name_en;
              updatedItem.name_ja = localLanguageMap[item.itemCode].name_ja;
              updatedItem.name_zh = localLanguageMap[item.itemCode].name_zh;
            }

            // 옵션 이름도 번역 적용 (menu_code:option_code 복합키로 조회)
            if (updatedItem.selectedOptions) {
              updatedItem.selectedOptions = updatedItem.selectedOptions.map(opt => {
                const compositeKey = `${item.itemCode}:${opt.itemCode}`;
                if (opt.itemCode && localLanguageMap[compositeKey]) {
                  return {
                    ...opt,
                    name_ko: localLanguageMap[compositeKey].name_ko,
                    name_en: localLanguageMap[compositeKey].name_en,
                    name_ja: localLanguageMap[compositeKey].name_ja,
                    name_zh: localLanguageMap[compositeKey].name_zh
                  };
                }
                return opt;
              });
            }

            return updatedItem;
          });

          const formattedOrder = {
            orderNumber: orderDetail.orderSum.OrderNo,
            timestamp: orderDetail.orderSum.FirstOrderDate,
            items: itemsWithLanguage
          };

          // 가공된 데이터 출력
          console.log('✅ === 가공된 주문 데이터 ===');
          console.log('formattedOrder:', JSON.stringify(formattedOrder, null, 2));
          console.log('=====================================\n');

          // API 데이터 사용
          setOrders([formattedOrder]);
        } else {
          // 주문 데이터가 없는 경우
          console.log('⚠️ Union API 주문내역 없음');
          console.log('  - orderDetail.success:', orderDetail.success);
          console.log('  - orderDetail.orderList:', orderDetail.orderList);
          setOrders([]);
        }
      } catch (error) {
        console.error('Union API 주문 내역 조회 실패:', error);
        setOrders([]);
      } finally {
        setIsLoading(false);
      }
    } catch (outerError) {
      // 외부 try 블록의 예상치 못한 에러 처리
      console.error('❌ loadOrderHistory 예상치 못한 에러:', outerError);
      setOrders([]);
      setIsLoading(false);
    }
  };

  const calculateTotalPrice = () => {
    return orders.reduce((total, order) => {
      return total + order.items.reduce((orderTotal, item) => {
        const price = item.totalPrice || item.price || 0;
        return orderTotal + (price * item.quantity);
      }, 0);
    }, 0).toLocaleString();
  };

  const getTableNumber = () => {
    // props로 전달받은 테이블 코드 사용
    return tableCode || localStorage.getItem('tableNumber') || '1';
  };

  // 선택된 옵션을 포맷팅하는 함수 (cartDrawer와 동일한 로직)
  const formatSelectedOptions = (item) => {
    const optionLines = [];

    // 고정세트 메뉴인 경우 옵션을 표시하지 않음
    if (item.menuType === '고정세트') {
      return optionLines; // 빈 배열 반환
    }

    // 메뉴에 hidden: true인 옵션이 있는 경우 (고정세트) 옵션을 표시하지 않음
    if (item.options && Array.isArray(item.options)) {
      const hasHiddenOptions = item.options.some(opt => opt.hidden === true);
      if (hasHiddenOptions) {
        return optionLines; // 빈 배열 반환
      }
    }
    
    if (item.selectedOptions) {
      // selectedOptions가 배열인 경우 (새로운 형식)
      if (Array.isArray(item.selectedOptions)) {
        // 옵션 그룹별로 정리
        const optionGroups = {};
        
        item.selectedOptions.forEach((option) => {
          const groupId = option.groupId || 'options';
          
          // groupId에서 _숫자 패턴 제거하여 순수한 그룹 이름 추출
          const cleanGroupId = groupId.replace(/_\d+$/, '');
          let groupName = cleanGroupId;
          
          if (!optionGroups[groupName]) {
            optionGroups[groupName] = [];
          }
          
          if (option && option.name) {
            const optionName = option[`name_${selectedLanguage}`] || option.name_ko || option[selectedLanguage]?.name || option.ko?.name || option.name;
            if (option.price && parseInt(option.price) > 0) {
              const formattedOption = selectedLanguage === 'ko' 
                ? `${optionName} (+${parseInt(option.price).toLocaleString()}원)`
                : `${optionName} (+₩${parseInt(option.price).toLocaleString()})`;
              optionGroups[groupName].push(formattedOption);
            } else {
              optionGroups[groupName].push(optionName);
            }
          }
        });
        
        // 각 그룹의 옵션들을 라인으로 추가 (한 그룹은 한 줄에)
        Object.entries(optionGroups).forEach(([groupName, options]) => {
          if (options.length > 0) {
            optionLines.push({
              title: groupName,
              content: options.join(' / ')
            });
          }
        });
      }
      // 객체 형식이지만 새 메뉴 형식
      else if (Array.isArray(item.options)) {
        // 옵션 그룹별로 정리
        const optionGroups = {};
        
        Object.entries(item.selectedOptions).forEach(([key, choice]) => {
          // key에서 _숫자 패턴 제거하여 순수한 그룹 이름만 추출
          const groupName = key.replace(/_\d+$/, '');
          if (!optionGroups[groupName]) {
            optionGroups[groupName] = [];
          }
          if (choice && choice.name) {
            if (choice.price && parseInt(choice.price) > 0) {
              const formattedOption = selectedLanguage === 'ko' 
                ? `${choice.name} (+${parseInt(choice.price).toLocaleString()}원)`
                : `${choice.name} (+₩${parseInt(choice.price).toLocaleString()})`;
              optionGroups[groupName].push(formattedOption);
            } else {
              optionGroups[groupName].push(choice.name);
            }
          }
        });
        
        // 각 그룹의 옵션들을 라인으로 추가
        Object.entries(optionGroups).forEach(([groupName, options]) => {
          if (options.length > 0) {
            optionLines.push({
              title: groupName,
              content: options.join(' / ')
            });
          }
        });
      }
      // 기존 형식 (객체)
      else if (item.options) {
        Object.entries(item.options).forEach(([optionTitle, optionData], index) => {
          const selected = item.selectedOptions[index];
          
          if (selected) {
            if (Array.isArray(selected)) {
              // 다중 선택 옵션
              const formattedItems = selected.map(optionName => {
                const optionInfo = optionData.items.find(opt => opt.name === optionName);
                if (optionInfo && optionInfo.price > 0) {
                  return selectedLanguage === 'ko' 
                    ? `${optionName} (${optionInfo.price.toLocaleString()}원)`
                    : `${optionName} (₩${optionInfo.price.toLocaleString()})`;
                }
                return optionName;
              });
              if (formattedItems.length > 0) {
                optionLines.push({
                  title: optionTitle,
                  content: formattedItems.join(' / ')
                });
              }
            } else {
              // 단일 선택 옵션
              const optionInfo = optionData.items.find(opt => opt.name === selected);
              let content = selected;
              if (optionInfo && optionInfo.price > 0) {
                content = selectedLanguage === 'ko'
                  ? `${selected} (${optionInfo.price.toLocaleString()}원)`
                  : `${selected} (₩${optionInfo.price.toLocaleString()})`;
              }
              optionLines.push({
                title: optionTitle,
                content: content
              });
            }
          }
        });
      }
    }
    
    return optionLines;
  };

  const groupOrdersByTime = () => {
    // 시간별로 그룹화
    const groups = [];
    orders.forEach(order => {
      const existingGroup = groups.find(g => g.time === order.time);
      if (existingGroup) {
        existingGroup.items.push(...order.items);
      } else {
        // 날짜 형식 변환
        let formattedTime = order.time;
        
        // timestamp가 있으면 사용, 없으면 현재 시간 기준으로 날짜 생성
        const date = order.timestamp ? new Date(order.timestamp) : new Date();
        
        // 기존 time 형식(HH:MM)에서 시간 추출 시도
        if (!order.timestamp && order.time && order.time.includes(':')) {
          const [hours, minutes] = order.time.split(':');
          date.setHours(parseInt(hours), parseInt(minutes));
        }
        
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const dayNames = ['일', '월', '화', '수', '목', '금', '토'];
        const dayOfWeek = dayNames[date.getDay()];
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        formattedTime = `${year}-${month}-${day} (${dayOfWeek}) ${hours}:${minutes}`;
        
        groups.push({
          time: formattedTime,
          orderNumber: order.orderNumber || 3,
          items: [...order.items]
        });
      }
    });
    return groups;
  };

  if (!isVisible) return null;

  return (
    <div className={`order-history ${isAnimating ? 'open' : ''}`}>
      <div className="order-history-wrapper">
        {orders.length === 0 && (
          <div className="back-button-container">
            <button className="back-button" onClick={() => onClose(false)}>
              <svg width="27" height="27" viewBox="0 0 27 27" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M5.625 13.5H21.375M5.625 13.5L12.375 20.25M5.625 13.5L12.375 6.75" stroke="black" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          </div>
        )}
        
        <div className={`order-history-content ${orders.length === 0 && !isLoading ? 'empty-state' : ''}`}>
          {isLoading ? (
            <div className="loading-container">
              <div className="loading-spinner"></div>
              <p className="loading-text">{selectedLanguage === 'ko' ? '주문내역을 불러오는 중...' : 'Loading orders...'}</p>
            </div>
          ) : orders.length > 0 ? (
            <div className="orders-list">
              {/* 상단 헤더 */}
              <div className="order-header">
                <div className="order-header-icon">
                  <img src={orderListIcon} alt="주문내역" />
                </div>
                <div className="order-header-text">
                  <h2 className="order-title">{t.orderTitle}</h2>
                  <p className="order-subtitle">{t.orderSubtitle}</p>
                </div>
              </div>

              {/* 총 가격과 테이블 번호 */}
              <div className="order-summary">
                <span className="total-price">
                  {selectedLanguage === 'ko' 
                    ? `${t.total} ${calculateTotalPrice()}원`
                    : `${t.total} ₩${calculateTotalPrice()}`
                  }
                </span>
                <div className="table-badge">
                  {selectedLanguage === 'en' || selectedLanguage === 'ja' ? `${t.table}${tableName}` : `${tableName}${t.table}`}
                </div>
              </div>

              {/* 주문 상세 내역 - 단일 컨테이너 */}
              <div className="order-details-container">
                <div className="order-section">
                  <div className="order-header-row">
                    <div className="order-number">
                      {selectedLanguage === 'ko'
                        ? `${t.orderNumber} ${orders[0]?.orderNumber || 3}`
                        : `${t.orderNumber}${orders[0]?.orderNumber || 3}`
                      }
                    </div>
                    <div className="order-time">
                      {(() => {
                        const order = orders[0];
                        if (!order) return '';

                        const date = order.timestamp ? new Date(order.timestamp) : new Date();
                        const year = date.getFullYear();
                        const month = String(date.getMonth() + 1).padStart(2, '0');
                        const day = String(date.getDate()).padStart(2, '0');
                        const dayOfWeek = t.weekdays[date.getDay()];
                        const hours = String(date.getHours()).padStart(2, '0');
                        const minutes = String(date.getMinutes()).padStart(2, '0');
                        return `${year}-${month}-${day} (${dayOfWeek}) ${hours}:${minutes}`;
                      })()}
                    </div>
                  </div>
                  <div className="order-items">
                    {(() => {
                      const allItems = [];
                      for (let i = 0; i < orders.length; i++) {
                        const items = orders[i].items || [];
                        for (let j = 0; j < items.length; j++) {
                          allItems.push(items[j]);
                        }
                      }
                      return allItems.map((item, itemIndex) => (
                        <div key={itemIndex} className="order-item">
                          <div className="order-item-main">
                            <span className="item-name">
                              {(() => {
                                // 언어별 메뉴 이름 처리
                                const langField = `name_${selectedLanguage}`;
                                const menuName = item[langField] ||
                                               item[`menu_${langField}`] ||
                                               item.name ||
                                               item.menuName ||
                                               item.menu_name ||
                                               'Unknown Item';
                                return menuName;
                              })()}
                            </span>
                            <span className="item-price">
                              {selectedLanguage === 'ko'
                                ? `${((item.totalPrice || item.price || 0) * item.quantity).toLocaleString()}원`
                                : `₩${((item.totalPrice || item.price || 0) * item.quantity).toLocaleString()}`
                              }
                            </span>
                          </div>
                          <div className="item-unit-price">
                            {selectedLanguage === 'ko'
                              ? `${(item.basePrice || item.price || 0).toLocaleString()}원`
                              : `₩${(item.basePrice || item.price || 0).toLocaleString()}`
                            }
                            {selectedLanguage === 'ko'
                              ? ` · ${item.quantity || 1}개`
                              : ` ${t.quantityFormat} ${item.quantity || 1}`
                            }
                          </div>
                          {/* 옵션 표시 - unit price 아래로 이동 */}
                          {(() => {
                            const options = formatSelectedOptions(item);
                            // "옵션" 그룹 하나만 있는 경우 한 줄로 표시
                            if (options.length === 1 && (options[0].title === '옵션' || options[0].title === 'options')) {
                              return (
                                <div className="item-option-line">
                                  <span className="option-prefix">ㄴ</span>
                                  <span className="option-content">
                                    {options[0].content}
                                  </span>
                                </div>
                              );
                            }
                            // 여러 옵션 그룹이 있는 경우 각각 표시
                            return options.map((option, optionIndex) => (
                              <div key={optionIndex} className="item-option-line">
                                <span className="option-prefix">ㄴ</span>
                                <span className="option-content">
                                  {option.title}: {option.content}
                                </span>
                              </div>
                            ));
                          })()}
                        </div>
                      ));
                    })()}
                    </div>
                    <div className="order-payment-info">
                      <span className="payment-text">
                        {selectedLanguage === 'ko'
                          ? `${t.payLater} · ${t.orderAmount}`
                          : `${t.payLater} · ${t.orderAmount}`
                        }
                        &nbsp;&nbsp;
                      </span>
                      <span className="payment-amount">
                        {selectedLanguage === 'ko'
                          ? `${(() => {
                              let total = 0;
                              for (let i = 0; i < orders.length; i++) {
                                const items = orders[i].items || [];
                                for (let j = 0; j < items.length; j++) {
                                  const item = items[j];
                                  total += (item.totalPrice || item.price || 0) * item.quantity;
                                }
                              }
                              return total.toLocaleString();
                            })()}원`
                          : `₩${(() => {
                              let total = 0;
                              for (let i = 0; i < orders.length; i++) {
                                const items = orders[i].items || [];
                                for (let j = 0; j < items.length; j++) {
                                  const item = items[j];
                                  total += (item.totalPrice || item.price || 0) * item.quantity;
                                }
                              }
                              return total.toLocaleString();
                            })()}`
                        }
                      </span>
                    </div>
                  </div>
                </div>
                {showCancelButton && (
                  <div className="order-cancel-wrap">
                    <button
                      type="button"
                      className="order-cancel-btn"
                      onClick={handleCancelOrder}
                      disabled={cancelling}
                    >
                      주문 취소
                    </button>
                  </div>
                )}
            </div>
          ) : (
            <div className="empty-orders">
              <img 
                src={emptyOrderlistIcon}
                alt="주문 내역 없음" 
                className="empty-orders-icon"
              />
              <p className="empty-orders-text">{t.emptyOrdersText}</p>
              {!hideOrderButton && (
              <button className="order-button"
                onMouseDown={(e) => e.currentTarget.classList.add('pressed')}
                onMouseUp={(e) => {
                  e.currentTarget.classList.remove('pressed');
                  // 버튼이 원래 크기로 돌아온 후 페이지 전환
                  setTimeout(() => {
                    // true를 전달하여 스크롤이 필요함을 알림
                    onClose(true);
                  }, 100);
                }}
                onTouchStart={(e) => e.currentTarget.classList.add('pressed')}
                onTouchEnd={(e) => {
                  e.currentTarget.classList.remove('pressed');
                  // 버튼이 원래 크기로 돌아온 후 페이지 전환
                  setTimeout(() => {
                    // true를 전달하여 스크롤이 필요함을 알림
                    onClose(true);
                  }, 100);
                }}
              >
                <svg width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect y="4.5" width="10" height="1" rx="0.5" fill="#000000"/>
                  <rect x="5.5" y="0" width="10" height="1" rx="0.5" transform="rotate(90 5.5 0)" fill="#000000"/>
                </svg>
                <span>{t.orderButton}</span>
              </button>
              )}
            </div>
          )}
        </div>

        {orders.length > 0 && (
        <div className={`fixed-bottom${showResetButton ? ' with-reset' : ''}`}>
          {showResetButton ? (
            <div className="reset-btn-col">
              <button
                className="bottom-button reset-btn"
                style={{ position: 'static', bottom: 'auto', margin: 0, flexShrink: 0, backgroundColor: 'rgb(255, 99, 101)', color: '#ffffff' }}
                onClick={async () => {
                  try {
                    const r = await resetWowposTable(propStoreId, tableCode);
                    if (!r?.success) throw new Error(r?.error || '초기화 실패');
                  } catch (e) {
                    console.error('[주문내역] 초기화 실패:', e);
                  }
                  onClose(false);
                }}
              >
                초기화
              </button>
              <button
                className="bottom-button back-btn"
                style={{ position: 'static', bottom: 'auto', margin: 0, flexShrink: 0 }}
                onClick={() => onClose(false)}
              >
                {bottomButtonLabel || t.backToMenu}
              </button>
            </div>
          ) : (
            <button className="bottom-button" onClick={() => onClose(false)}>
              {bottomButtonLabel || t.backToMenu}
            </button>
          )}
        </div>
      )}
      </div>
      
      

      <style jsx="true">{`
        .order-history {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: white;
          z-index: 1000;
          transform: translateX(100%);
          transition: transform 0.15s cubic-bezier(0.4, 0.0, 0.2, 1);
          overflow: hidden;
        }

        .order-history.open {
          transform: translateX(0);
        }

        .order-history-wrapper {
          width: 360px;
          height: 100%;
          height: 100vh;
          height: 100dvh;
          margin: 0 auto;
          position: relative;
          display: flex;
          flex-direction: column;
          transform-origin: top center;
          overflow: hidden;
        }

        .back-button-container {
          position: absolute;
          top: 14px;
          left: 16px;
          z-index: 10;
        }
        
        .back-button {
          background: none;
          border: none;
          padding: 0;
          cursor: pointer;
          width: 27px;
          height: 27px;
          display: block;
        }
        
        .back-button svg {
          width: 100%;
          height: 100%;
          display: block;
        }

        .order-history-content {
          flex: 1;
          overflow-y: auto;
          scrollbar-width: none;
          -ms-overflow-style: none;
          padding-bottom: 200px;
          overscroll-behavior: contain;
          -webkit-overflow-scrolling: touch;
        }
        
        .order-history-content::-webkit-scrollbar {
          display: none;
        }
        
        .order-history-content.empty-state {
          overflow: hidden;
        }

        .empty-orders {
          display: flex;
          flex-direction: column;
          align-items: center;
          height: 100%;
          margin-top: 200px;
        }
        
        .empty-orders-icon {
          width: 92px;
          height: 92px;
        }
        
        .empty-orders-text {
          margin-top: 8px;
          margin-bottom: 0;
          font-size: 18px;
          font-weight: 600;
          color: #949494;
        }
        
        .order-button {
          margin-top: 24px;
          padding: 0 12px;
          width: auto;
          height: 31px;
          border: 1px solid #000000;
          border-radius: 20px;
          background: white;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 4px;
          cursor: pointer;
          transition: all 0.05s ease;
        }
        
        .order-button.pressed {
          transform: scale(0.92);
          background: #dbdbdb;
        }
        
        .order-button svg {
          width: 10px;
          height: 10px;
        }
        
        .order-button span {
          font-size: 14px;
          font-weight: 400;
          color: #333333;
        }
        
        /* 주문 내역이 있을 때 스타일 */
        .orders-list {
          padding: 0 16px;
          margin-bottom: 350px;
        }
        
        .order-header {
          margin-top: 24px;
          display: flex;
          align-items: flex-start;
        }

        .order-cancel-wrap {
          margin-top: 12px;
          display: flex;
          justify-content: center;
        }
        .order-cancel-btn {
          padding: 4px 2px;
          border: none;
          background: transparent;
          color: #e0393b;
          font-size: 13px;
          font-weight: 600;
          font-family: inherit;
          cursor: pointer;
          text-decoration: underline;
          text-underline-offset: 2px;
          white-space: nowrap;
          transition: opacity 0.12s;
        }
        .order-cancel-btn:active { opacity: 0.6; }
        .order-cancel-btn:disabled { opacity: 0.4; cursor: default; }

        .order-header-icon {
          width: 64px;
          height: 64px;
          flex-shrink: 0;
        }
        
        .order-header-icon img {
          width: 100%;
          height: 100%;
        }
        
        .order-header-text {
          flex: 1;
          display: flex;
          flex-direction: column;
          padding: 10px 0;
        }
        
        .order-title {
          font-size: 20px;
          font-weight: 600;
          margin: 0;
          color: #000000;
          padding-left: 1px;
        }
        
        .order-subtitle {
          margin-top: 6px;
          margin-bottom: 0;
          font-size: 12px;
          font-weight: 500;
          color: #FF5C5C;
          padding-left: 1px;
        }
        
        .order-summary {
          margin-top: 24px;
          padding: 0 10px;
          display: flex;
          justify-content: space-between;
          align-items: center;
          width: 328px;
        }
        
        .total-price {
          font-size: 20px;
          font-weight: 700;
          color: #000000;
        }
        
        .table-badge {
            padding: 0 5px;
            width: auto;
            height: 24px;
            background-color: #f1f1f1;
            color: #959595;
            border-radius: 6px;
            font-size: 12px;
            font-weight: 400;
            display: flex;
            align-items: center;
            justify-content: center;
            line-height: 14px;
        }
        
        .table-badge span:first-child {
          color: #666666;
        }
        
        .table-badge span:last-child {
          color: #000000;
        }
        
        .order-details-container {
          margin-top: 20px;
          border: 1.2px solid #E4E6E8;
          border-radius: 10px;
          padding: 24px 18px 20px;
        }
        
        .order-details-container:first-of-type {
          margin-top: 20px;
        }

        .order-section {
          padding-bottom: 0;
          margin-bottom: 0;
        }
        
        .order-header-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding-bottom: 20px;
          margin-bottom: 20px;
          border-bottom: 1.2px solid #F3F5F7;
        }
        
        .order-number {
          font-size: 14px;
          font-weight: bold;
          color: #000000;
          background-color: rgba(253, 205, 39, 0.5);
          display: inline-block;
        }
        
        .order-time {
          font-size: 12px;
          font-weight: 400;
          color: #B3B3B3;
        }
        
        .order-items {
          display: flex;
          flex-direction: column;
          gap: 16px;
        }
        
        .order-item:last-child {
          padding-bottom: 20px;
          border-bottom: 1.2px solid #F3F5F7;
        }
        
        .order-item-main {
          display: flex;
          justify-content: space-between;
          align-items: center;
        }
        
        .item-name {
          max-width: 210px;
          font-size: 14px;
          font-weight: 500;
          color: #000000;
        }
        
        .item-price {
          font-size: 14px;
          font-weight: 500;
          color: #000000;
        }
        
        .item-option-line {
          display: flex;
          align-items: flex-start;
          padding-left: 0;
          max-width: 222px;
        }
        
        .option-prefix {
          font-size: 12px;
          font-weight: 400;
          color: #B3B3B3;
          margin-right: 4px;
          flex-shrink: 0;
          line-height: 16px;
        }
        
        .option-content {
          font-size: 12px;
          font-weight: 400;
          color: #B3B3B3;
          line-height: 16px;
        }
        
        .item-unit-price {
          margin-top: 4px;
          font-size: 12px;
          font-weight: 400;
          color: #B3B3B3;
          line-height: 16px;
        }
        
        .order-payment-info {
          margin-top: 20px;
          text-align: center;
        }
        
        .payment-text {
          font-size: 14px;
          font-weight: 500;
          color: #000000;
        }
        
        .payment-amount {
          font-size: 14px;
          font-weight: bold;
          color: #000000;
        }
        
        .fixed-bottom {
          background-image: url(${bottomNavBg});
          position: fixed;
          bottom: 0;
          left: 50%;
          transform: translateX(-50%);
          width: 360px;
          height: 100px;
          display: flex;
          justify-content: center;
          align-items: flex-end;
          padding-bottom: 25px;
          z-index: 1000;
          pointer-events: none;
        }
        
         @media screen and (min-width: 361px) {
          .order-history-wrapper {
            zoom: calc(100vw / 360);
          }
        }
        
        @media screen and (min-width: 540px) {
          .order-history-wrapper {
            zoom: 1.5;
          }
        }
        
        @media screen and (max-width: 360px) {
          .order-history-wrapper {
            width: 100vw;
            zoom: calc(100vw / 360);
          }
        }
        
        .bottom-button {
          width: 328px;
          height: 50px;
          background-color: #fdcd27;
          border-radius: 10px;
          border: none;
          font-size: 17px;
          font-weight: 700;
          color: #ffffff;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          letter-spacing: -0.32px;
          line-height: 19px;
          transition: transform 0.12s cubic-bezier(0.4, 0.0, 0.2, 1);
          pointer-events: auto;
        }
        
        .bottom-button:active {
          transform: scale(0.96);
        }

        /* 관리자(와우포스) 상세 — 초기화/뒤로가기 2버튼 세로 스택 */
        .fixed-bottom.with-reset {
          height: 170px;
          padding-bottom: 0;
          margin-bottom: 12px;
          background-size: 100% 100%;
          background-repeat: no-repeat;
        }
        .reset-btn-col {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 9px;
          pointer-events: auto;
        }
        .bottom-button.reset-btn {
          background-color: #e0393b;
          color: #d9d9d9;
        }
        .bottom-button.back-btn {
          background-color: #f1f1f1;
          color: #959595;
        }

        /* 로딩 스피너 스타일 */
        .loading-container {
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: center;
          height: 400px;
          gap: 20px;
        }

        .loading-spinner {
          width: 40px;
          height: 40px;
          border: 4px solid #f3f3f3;
          border-top: 4px solid #fdcd27;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        .loading-text {
          font-size: 14px;
          color: #666;
          margin: 0;
        }

      `}</style>
    </div>
  );
};

export default OrderHistory;